import React from "react";
import { QueryClient, useQuery } from "@tanstack/react-query";
import { ClientFactory } from "@/main/common/ClientFactory";
import {
    FAVORITES_KEY,
    type GroupList,
    customListsOf,
    favoritesOf,
    normalizeGroupList,
    normalizeGroupLists,
    removeListFrom,
    upsertList,
} from "./groupListsModel";

/**
 * The member's group lists, owned by ONE react-query cache entry (SV-144 `2a`, A13).
 *
 * <p><b>A module-level QueryClient, not the chat one, and that is the rule-31 half of A13.</b> The lists
 * are read in Messages, on the group page and on every group card; the group page and the cards have no
 * chat QueryClient above them (each Messages shell makes its own). A copy per surface would be two stores
 * holding one value, so there is one client, created on first use, living as long as the page.
 *
 * <p>`notifyOnChangeProps` is an explicit allowlist (`data`, `status`) for the same reason as the chat
 * thread query: `fetchStatus` and `dataUpdatedAt` change on every fetch by construction.
 *
 * <p>Every write takes the server's returned list as the new value; nothing is written optimistically,
 * so the cache never holds a state the server refused.
 */
export const GROUP_LISTS_QUERY_KEY = ["group-lists"] as const;

let groupListsQueryClient: QueryClient | null = null;

export function getGroupListsQueryClient(): QueryClient {
    if (!groupListsQueryClient) {
        groupListsQueryClient = new QueryClient({
            defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
        });
    }
    return groupListsQueryClient;
}

/** Test seam: drop the singleton so each spec starts from an empty cache. */
export function resetGroupListsQueryClientForTests(): void {
    groupListsQueryClient?.clear();
    groupListsQueryClient = null;
}

function api() {
    return ClientFactory.getAjaxGroupListRestControllerApi();
}

async function fetchGroupLists(): Promise<GroupList[]> {
    const response = await api().getLists();
    return normalizeGroupLists(Array.isArray(response.data) ? response.data : []);
}

const EMPTY_LISTS: GroupList[] = [];

/**
 * The lists, for a surface that renders them. `enabled` is false for an anonymous visitor or a
 * non-participant (A14): nothing is fetched and nothing new renders.
 */
export function useGroupLists(enabled: boolean = true) {
    const query = useQuery({
        queryKey: GROUP_LISTS_QUERY_KEY,
        queryFn: fetchGroupLists,
        enabled,
        staleTime: 60_000,
        notifyOnChangeProps: ["data", "status"],
    }, getGroupListsQueryClient());
    const lists = query.data ?? EMPTY_LISTS;
    const favorites = React.useMemo(() => favoritesOf(lists), [lists]);
    const customLists = React.useMemo(() => customListsOf(lists), [lists]);
    // FI-806: `refetch` is the failed state's retry (stable across renders; TanStack binds it once).
    return { lists, favorites, customLists, loaded: query.status === "success", status: query.status, refetch: query.refetch };
}

export function ensureGroupLists(): Promise<GroupList[]> {
    return getGroupListsQueryClient().fetchQuery({ queryKey: GROUP_LISTS_QUERY_KEY, queryFn: fetchGroupLists, staleTime: 60_000 });
}

function writeLists(update: (previous: GroupList[]) => GroupList[]): void {
    getGroupListsQueryClient().setQueryData<GroupList[]>(GROUP_LISTS_QUERY_KEY, (previous) => {
        const base = previous ?? normalizeGroupLists([]);
        return update(base);
    });
}

function takeList(data: unknown): GroupList {
    const list = normalizeGroupList(data as never);
    writeLists((previous) => upsertList(previous, list));
    return list;
}

/** Add or remove one group from one list (Favorites included). Idempotent on the server. */
export async function setGroupInList(listKey: string, groupPublicId: string, inList: boolean): Promise<GroupList> {
    const response = inList
        ? await api().addGroup(listKey, groupPublicId)
        : await api().removeGroup(listKey, groupPublicId);
    return takeList(response.data);
}

export function setGroupFavorite(groupPublicId: string, favorite: boolean): Promise<GroupList> {
    return setGroupInList(FAVORITES_KEY, groupPublicId, favorite);
}

export async function createGroupList(name: string): Promise<GroupList> {
    const response = await api().createList({ name });
    return takeList(response.data);
}

export async function renameGroupList(listKey: string, name: string): Promise<GroupList> {
    const response = await api().renameList(listKey, { name });
    return takeList(response.data);
}

export async function deleteGroupList(listKey: string): Promise<void> {
    await api().deleteList(listKey);
    writeLists((previous) => removeListFrom(previous, listKey));
}

/** The full manual order (D7); the server refuses anything that is not a permutation of the entries. */
export async function reorderGroupList(listKey: string, groupPublicIds: string[]): Promise<GroupList> {
    const response = await api().reorder1(listKey, { groupPublicIds });
    return takeList(response.data);
}

/** Select mode's bulk apply: all or nothing on the server. */
export async function applyGroupListBatch(listKey: string, add: string[], remove: string[]): Promise<GroupList> {
    const response = await api().applyBatch(listKey, { add, remove });
    return takeList(response.data);
}

/**
 * D13: one call over the viewer's memberships' existing board mute. Returns what the server stored per
 * group (a leader-only row reports `muted: false`: it has no mute switch).
 */
export async function setGroupsMuted(groupPublicIds: string[], muted: boolean): Promise<Array<{ groupPublicId: string; muted: boolean }>> {
    const response = await ClientFactory.getAjaxGroupBoardRestControllerApi().setMutedForGroups({ groupPublicIds, muted });
    const rows = Array.isArray(response.data) ? response.data as Array<{ groupPublicId?: string; muted?: boolean }> : [];
    return rows
        .filter((row) => typeof row?.groupPublicId === "string")
        .map((row) => ({ groupPublicId: row.groupPublicId as string, muted: row.muted === true }));
}
